import { useState } from "react";

const checklistItems = [
  "Warm white or off-white walls with a yellow or pink undertone",
  "Beige or cream sofa in linen, bouclé, or soft cotton",
  "Cream or ivory area rug in wool, jute, or low-pile cotton",
  "Light oak or ash wood coffee table",
  "Sheer linen curtains in off-white or sand",
  "One warm floor lamp near the seating area",
  "Two to four textured cushions in layered neutral tones",
  "One matte ceramic vase or decorative piece",
  "One small plant or dried botanical stem",
];

const faqs = [
  {
    id: "faq-1",
    question: "How do I make a beige living room feel warm instead of boring?",
    answer:
      "The key is layering. A single flat beige tone on every surface will feel flat and lifeless — but when you layer different textures (linen, wool, wood, ceramic) and slightly different warm tones (cream, ivory, oatmeal, sand), the room gains depth and character. Warm lighting also makes a significant difference: use bulbs in the 2700K–3000K range rather than cool white. Finally, add one or two natural elements — a dried stem, a small plant, a wooden bowl — to give the room something organic and alive to hold onto.",
  },
  {
    id: "faq-2",
    question: "What colors pair best with soft beige interiors?",
    answer:
      "Soft beige pairs most naturally with warm whites, cream, ivory, sand, and taupe — all within the same warm neutral family. For gentle contrast, warm terracotta, dusty sage, or muted clay tones work beautifully without disrupting the calm. Avoid cool greys, bright whites, or saturated colours, as these create visual tension that works against the relaxed quality of a beige interior. If you want to introduce a deeper tone, warm charcoal or dark espresso in a single accent piece (a lamp base, a picture frame, a side table leg) adds quiet definition without overpowering the palette.",
  },
  {
    id: "faq-3",
    question: "What materials work best in a calm neutral living room?",
    answer:
      "Natural materials are the foundation of a calm neutral interior. Linen and cotton for upholstery and curtains, wool and jute for rugs, light oak or ash for furniture, matte ceramic for decorative pieces, and rattan or woven textures for accent items. These materials share a quality of warmth and imperfection that synthetic alternatives lack — they absorb light softly, age gracefully, and feel genuinely comfortable to be around. Avoid high-gloss surfaces, chrome, and anything that reflects light sharply, as these introduce a visual sharpness that works against the calm.",
  },
];

export default function ArticleFormula() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4">
      {/* Visual formula checklist */}
      <div className="bg-[#FAF7F0] rounded-2xl p-8 md:p-12 mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-5"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Visual Reference
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-8"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          A simple formula for a soft beige living room
        </h2>
        <ul className="space-y-4">
          {checklistItems.map((item, i) => (
            <li key={i} className="flex items-center gap-4">
              <span
                className="w-6 h-6 flex items-center justify-center rounded-full border border-[#C8BAA8] text-[#B5A898] flex-shrink-0"
                style={{ fontFamily: "'Inter', sans-serif", fontSize: "10px" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className="text-[#4A3F38] text-sm"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* FAQ Section */}
      <div className="mb-16">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-5"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Frequently Asked
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-10"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Common questions about soft beige living rooms
        </h2>
        <div className="space-y-0 border-t border-[#EDE6DA]">
          {faqs.map((faq) => (
            <div key={faq.id} className="border-b border-[#EDE6DA]">
              <button
                className="w-full flex items-start justify-between gap-6 py-6 text-left cursor-pointer group"
                onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
              >
                <span
                  className="text-[#2C2825] text-base leading-snug group-hover:text-[#6B5E53] transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  {faq.question}
                </span>
                <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i
                    className={`ri-${openFaq === faq.id ? "subtract" : "add"}-line text-[#B5A898] text-base transition-transform duration-200`}
                  />
                </span>
              </button>
              {openFaq === faq.id && (
                <div className="pb-7 -mt-2">
                  <p
                    className="text-[#6B5E53] text-sm leading-[1.9]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                  >
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Soft conclusion */}
      <div className="pb-16 border-b border-[#EDE6DA]">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-5"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Final Thoughts
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          A room that gets better with time
        </h2>
        <p
          className="text-[#6B5E53] text-base leading-[1.9] mb-6"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          A calm living room does not need dramatic design choices to feel beautiful. Soft beige, natural light, quiet textures, and simple furniture are enough to create a space that feels timeless, restful, and deeply liveable. The real design work in a beige interior is in the editing — in choosing fewer things, placing them with care, and leaving room for light and air to do their part.
        </p>
        <p
          className="text-[#6B5E53] text-base leading-[1.9]"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          Start with the basics, trust the palette, and let the room settle into itself over time. The best soft beige living rooms are not finished in a weekend — they are built slowly, piece by piece, with patience and a clear sense of what the space is for. That unhurried quality is exactly what makes them feel so good to come home to.
        </p>
      </div>
    </section>
  );
}
